import { useRef, useEffect, useMemo } from 'react'
import type { TrajectoryInfo } from '@/types/forklift'

type PathPoint = [number, number] | { x: number; y: number }

function getCoords(point: PathPoint): { x: number; y: number } {
  if (Array.isArray(point)) {
    return { x: point[0], y: point[1] }
  }
  return { x: point.x, y: point.y }
}

const PATH_COLORS = [
  '#3b82f6',
  '#10b981',
  '#f59e0b',
  '#ef4444',
  '#8b5cf6',
  '#06b6d4',
]

export default function PreviewPathCanvas({
  trajectories,
  width = 400,
  height = 250,
}: {
  trajectories: TrajectoryInfo[]
  width?: number
  height?: number
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  const pathsWithColors = useMemo(() => {
    return trajectories
      .filter((t) => t.path && Array.isArray(t.path) && t.path.length > 0)
      .map((t, i) => ({
        trajectory: t,
        color: PATH_COLORS[i % PATH_COLORS.length],
      }))
  }, [trajectories])

  const bounds = useMemo(() => {
    let minX = Infinity,
      maxX = -Infinity
    let minY = Infinity,
      maxY = -Infinity

    for (const { trajectory } of pathsWithColors) {
      for (const point of trajectory.path!) {
        const { x, y } = getCoords(point)
        if (x < minX) minX = x
        if (x > maxX) maxX = x
        if (y < minY) minY = y
        if (y > maxY) maxY = y
      }
    }

    const MIN_RANGE = 2
    if (maxX - minX < MIN_RANGE) {
      const cx = (minX + maxX) / 2
      minX = cx - MIN_RANGE / 2
      maxX = cx + MIN_RANGE / 2
    }
    if (maxY - minY < MIN_RANGE) {
      const cy = (minY + maxY) / 2
      minY = cy - MIN_RANGE / 2
      maxY = cy + MIN_RANGE / 2
    }

    return { minX, maxX, minY, maxY }
  }, [pathsWithColors])

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas || pathsWithColors.length === 0) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const w = canvas.width
    const h = canvas.height
    const padding = 20

    ctx.clearRect(0, 0, w, h)
    ctx.fillStyle = '#f8fafc'
    ctx.fillRect(0, 0, w, h)

    const drawWidth = w - padding * 2
    const drawHeight = h - padding * 2
    const xRange = bounds.maxX - bounds.minX
    const yRange = bounds.maxY - bounds.minY

    const scaleX = drawWidth / xRange
    const scaleY = drawHeight / yRange
    const scale = Math.min(scaleX, scaleY)

    const offsetX = padding + (drawWidth - xRange * scale) / 2
    const offsetY = padding + (drawHeight - yRange * scale) / 2

    const transform = (x: number, y: number) => ({
      x: offsetX + (x - bounds.minX) * scale,
      y: offsetY + (bounds.maxY - y) * scale,
    })

    // グリッド
    ctx.strokeStyle = '#e2e8f0'
    ctx.lineWidth = 1
    const gridStep = Math.pow(
      10,
      Math.floor(Math.log10(Math.max(xRange, yRange)))
    )
    const xStart = Math.ceil(bounds.minX / gridStep) * gridStep
    const yStart = Math.ceil(bounds.minY / gridStep) * gridStep

    for (let x = xStart; x <= bounds.maxX; x += gridStep) {
      const { x: cx } = transform(x, 0)
      ctx.beginPath()
      ctx.moveTo(cx, padding)
      ctx.lineTo(cx, h - padding)
      ctx.stroke()
    }
    for (let y = yStart; y <= bounds.maxY; y += gridStep) {
      const { y: cy } = transform(0, y)
      ctx.beginPath()
      ctx.moveTo(padding, cy)
      ctx.lineTo(w - padding, cy)
      ctx.stroke()
    }

    // 各パスを描画
    for (const { trajectory, color } of pathsWithColors) {
      const path = trajectory.path!
      if (path.length < 2) continue

      ctx.strokeStyle = color
      ctx.lineWidth = 2
      ctx.lineCap = 'round'
      ctx.lineJoin = 'round'
      ctx.beginPath()

      for (let i = 0; i < path.length; i++) {
        const { x, y } = getCoords(path[i])
        const pos = transform(x, y)
        if (i === 0) {
          ctx.moveTo(pos.x, pos.y)
        } else {
          ctx.lineTo(pos.x, pos.y)
        }
      }
      ctx.stroke()

      // 開始点
      const startCoord = getCoords(path[0])
      const start = transform(startCoord.x, startCoord.y)
      ctx.fillStyle = '#22c55e'
      ctx.beginPath()
      ctx.arc(start.x, start.y, 5, 0, Math.PI * 2)
      ctx.fill()

      // 終了点
      const endCoord = getCoords(path[path.length - 1])
      const end = transform(endCoord.x, endCoord.y)
      ctx.fillStyle = '#ef4444'
      ctx.beginPath()
      ctx.arc(end.x, end.y, 5, 0, Math.PI * 2)
      ctx.fill()
    }
  }, [pathsWithColors, bounds])

  if (pathsWithColors.length === 0) {
    return (
      <div className="flex items-center justify-center h-40 bg-gray-100 rounded-lg text-gray-500 text-sm">
        パス情報がありません
      </div>
    )
  }

  return (
    <div className="space-y-2">
      <canvas
        ref={canvasRef}
        width={width}
        height={height}
        className="w-full border border-gray-200 rounded-lg"
      />
      <div className="flex gap-4 text-xs text-gray-500">
        <div className="flex items-center gap-1">
          <div className="w-2 h-2 rounded-full bg-green-500" />
          <span>開始点</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-2 h-2 rounded-full bg-red-500" />
          <span>終了点</span>
        </div>
      </div>
    </div>
  )
}
